import { useState, useEffect } from 'react';
import { useAuth } from "../../../contexts/AuthContext";
import { db } from "../../../firebase";
import { doc, getDoc, runTransaction } from "firebase/firestore";
import getServerTimestamp from '../../../functions/endpoints/GetServerTimestamp';
import { useNavigate, useParams } from "react-router";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import ErrorDisplay from '../../../components/ErrorDisplay';
import Backdrop from "@mui/material/Backdrop";
import * as theme from '../../../theme';
import dayjs from 'dayjs';
import Button from "@mui/material/Button";
import GeneralSelect from '../../../components/GeneralSelect';
import AvailableScanDates from '../../../components/schedule/AvailableScanDates';
import { scanList } from '../../../contexts/FocusScanContext';
import { miniScanList } from '../../../contexts/MiniScanContext';
import { fetchInitialScanDates, fetchMoreDates, handleDateSelect } from '../../../functions/scheduleUtils';
import AddableList from '../../../components/AddableList';
import TextField from "@mui/material/TextField";
import ConfirmationModal from '../../../components/ConfirmationModal';
import { Info } from '@mui/icons-material';
import FocusScansList from '../../../components/FocusScansList';
import MiniScansList from '../../../components/MiniScansList';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

export default function EditScan() {
  const [formData, setFormData] = useState({
    scanDate: null,
    concern: [],
    supplements: [],
    firstFocusScan: '',
    secondFocusScan: '',
    firstMiniScan: '',
    secondMiniScan: '',
    multiHomeDiscount: false, // New field for multi-home discount flag
  });
  const [originalScanDate, setOriginalScanDate] = useState(null);
  const [scan, setScan] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDateIndex, setSelectedDateIndex] = useState(null);
  const [hoveredDateIndex, setHoveredDateIndex] = useState(null);
  const [lastFetchedDate, setLastFetchedDate] = useState(null);
  const [focusInfoModalOpen, setFocusInfoModalOpen] = useState(false);
  const [miniInfoModalOpen, setMiniInfoModalOpen] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { clientId, scanId } = useParams();

  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    const getScan = async () => {
      try {
        const scanRef = doc(db, "clients", clientId, "scans", scanId);
        const docSnap = await getDoc(scanRef);
        if (docSnap.exists()) {
          const scanData = docSnap.data();
          if (scanData.cancelled) {
            navigate(`/clients/${clientId}/scans/${scanId}`);
          }
          let concern = scanData.concern || [];
          if (typeof concern === 'string') {
            concern = concern.split(',').map(s => s.trim()).filter(Boolean);
          }
          let supplements = scanData.supplements || [];
          if (typeof supplements === 'string') {
            supplements = supplements.split(',').map(s => s.trim()).filter(Boolean);
          }
          setFormData({
            scanDate: scanData.scanDate ? dayjs(scanData.scanDate) : null,
            concern,
            supplements,
            firstFocusScan: scanData.firstFocusScan || '',
            secondFocusScan: scanData.secondFocusScan || '',
            firstMiniScan: scanData.firstMiniScan || '',
            secondMiniScan: scanData.secondMiniScan || '',
            multiHomeDiscount: scanData.multiHomeDiscount || false, // Load the multi-home discount flag
          });
          setOriginalScanDate(scanData.scanDate ? dayjs(scanData.scanDate) : null);
          setScan(scanData);
        } else {
          setError("Scan not found.");
        }
      } catch (err) {
        setError("Failed to get scan: " + err.message);
      }
    };

    if (scanId) {
      getScan();
    }
  }, [scanId, clientId, navigate]);

  useEffect(() => {
    const fetchDates = async () => {
      try {
        let minDate = dayjs();
        const proposedMinDate = currentUser.role !== 'practitioner' ? dayjs().startOf('day') : dayjs(scan.dateCreated.toDate()).add(12, 'day').startOf('day');
        if (proposedMinDate.isAfter(dayjs())) {
          minDate = proposedMinDate;
        }

        await fetchInitialScanDates(minDate, setAvailableDates, setLastFetchedDate, setError);
      } catch (err) {
        setError("Failed to fetch available dates: " + err.message);
      }
    };

    if (scan.scanPaid) {
      navigate(`/clients/${clientId}/scans/${scanId}`);
    }

    if (scan.dateCreated) {
      fetchDates();
    }
  }, [scan, navigate, clientId, scanId]);

  useEffect(() => {
    if (selectedDateIndex === null && formData.scanDate && availableDates.length > 0) {
      const index = availableDates.findIndex((item) => item.date.isSame(formData.scanDate, 'day'));
      if (index !== -1) {
        setSelectedDateIndex(index);
      }
    }
  }, [formData.scanDate, availableDates, selectedDateIndex]);

  const addItem = (field, value) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], value.trim()]
    });
  };

  const removeItem = (field, index) => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updated });
  };

  const handleCheckboxChange = (event) => {
    setFormData({
      ...formData,
      multiHomeDiscount: event.target.checked,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.scanDate) {
      setError("Please select a scan date.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const timestamp = await getServerTimestamp();

      const scanData = {
        scanDate: formData.scanDate ? formData.scanDate.format('YYYY-MM-DD') : null,
        concern: formData.concern,
        supplements: formData.supplements,
        firstFocusScan: formData.firstFocusScan || '',
        secondFocusScan: formData.secondFocusScan || '',
        firstMiniScan: formData.firstMiniScan || '',
        secondMiniScan: formData.secondMiniScan || '',
        dateModified: timestamp,
        multiHomeDiscount: formData.multiHomeDiscount, // Save the multi-home discount flag
      };

      await runTransaction(db, async (transaction) => {
        const clientScansRef = doc(db, "clients", clientId, "scans", scanId);
        transaction.update(clientScansRef, scanData);

        const originalDateTimestamp = originalScanDate ? originalScanDate.format('YYYY-MM-DD') : null;
        const newDateTimestamp = scanData.scanDate;

        if (originalDateTimestamp !== newDateTimestamp) {
          const clientRef = doc(db, "clients", clientId);
          const clientSnap = await getDoc(clientRef);
          if (clientSnap.exists()) {
            const clientData = clientSnap.data();
            const updatedScans = clientData.scans.map((scan) => {
              if (scan.id === scanId) {
                return {
                  ...scan,
                  scanDate: scanData.scanDate,
                };
              }
              return scan;
            });
            transaction.update(clientRef, { scans: updatedScans });
          }

          if (originalDateTimestamp) {
            const oldMonthKey = dayjs(originalDateTimestamp).format('YYYYMM');
            const oldDayKey = dayjs(originalDateTimestamp).format('DD');
            const oldScheduleDocRef = doc(db, 'schedule', oldMonthKey);
            const oldScheduleDocSnap = await getDoc(oldScheduleDocRef);
            if (oldScheduleDocSnap.exists()) {
              const oldMonthData = oldScheduleDocSnap.data();
              const oldDayData = oldMonthData.days[oldDayKey] || {
                date: dayjs(originalDateTimestamp).format('YYYY-MM-DD'),
                practitioners: [],
                scheduled: [],
                scansBooked: 0,
                totalScans: 0,
              };
              const updatedOldDayData = {
                ...oldDayData,
                scheduled: oldDayData.scheduled.filter((s) => s.scanId !== scanId),
                scansBooked: Math.max(0, oldDayData.scansBooked - 1),
              };
              const updatedOldMonthData = {
                ...oldMonthData,
                days: {
                  ...oldMonthData.days,
                  [oldDayKey]: updatedOldDayData,
                },
              };
              transaction.set(oldScheduleDocRef, updatedOldMonthData, { merge: true });
            }
          }

          const monthKey = formData.scanDate.format('YYYYMM');
          const dayKey = formData.scanDate.format('DD');
          const scheduleDocRef = doc(db, 'schedule', monthKey);
          const scheduleDocSnap = await getDoc(scheduleDocRef);
          const monthData = scheduleDocSnap.exists()
            ? scheduleDocSnap.data()
            : { month: `${monthKey.slice(0, 4)}-${monthKey.slice(4)}`, days: {} };
          const dayData = monthData.days[dayKey] || {
            date: formData.scanDate.format('YYYY-MM-DD'),
            practitioners: [],
            scheduled: [],
            scansBooked: 0,
            totalScans: 0,
          };
          const updatedDayData = {
            ...dayData,
            scheduled: [
              ...dayData.scheduled,
              {
                scanId,
                clientId,
                practitionerId: clientSnap.data().practitionerId,
                timestamp: timestamp,
              },
            ],
            scansBooked: dayData.scansBooked + 1,
          };
          const updatedMonthData = {
            ...monthData,
            days: {
              ...monthData.days,
              [dayKey]: updatedDayData,
            },
          };
          transaction.set(scheduleDocRef, updatedMonthData, { merge: true });

          let practitionerId = null;

          const activeScansRef = doc(db, "app", "activeScans");
          const activeScansSnap = await getDoc(activeScansRef);
          const activeScansData = activeScansSnap.exists() ? activeScansSnap.data() : { scans: [] };
          const updatedScans = activeScansData.scans.map((scan) => {
            if (scan.id === scanId) {
              practitionerId = scan.practitionerId;
              return { ...scan, date: scanData.scanDate, modifiedDate: timestamp };
            }
            return scan;
          });
          transaction.set(activeScansRef, {
            scans: updatedScans
          }, { merge: true });

          if (practitionerId != null) {
            const practitionerScansRef = doc(db, "activeScans", practitionerId);
            const practitionerScansSnap = await getDoc(practitionerScansRef);
            const practitionerScansData = practitionerScansSnap.exists() ? practitionerScansSnap.data() : { scans: [] };
            const practitionerScans = practitionerScansData.scans.map((scan) => {
              if (scan.id === scanId) {
                return { ...scan, date: scanData.scanDate, modifiedDate: timestamp };
              }
              return scan;
            });
            transaction.set(practitionerScansRef, {
              scans: practitionerScans
            }, { merge: true });
          }
        }
      });

      navigate(`/clients/${clientId}/scans/${scanId}`);
    } catch (err) {
      setError("Failed to edit scan: " + err.message);
    }

    setLoading(false);
  };

  const today = dayjs();
  const maxDate = today.add(3, 'month').endOf('day');
  const showMoreButtonVisible = lastFetchedDate && lastFetchedDate.isBefore(maxDate);

  return (
    <div style={theme.Container.basic}>
      <div style={theme.Heading}>EDIT/RESCHEDULE SCAN</div>
      <div style={theme.SubHeadingText}>
        Please select a scan date at least two weeks from today to allow sufficient time for mailing the kit to your client and for them to return it. The kit must be mailed back at least five days before the scheduled scan date.
      </div>
      <div style={{ marginTop: 15, marginBottom: 30, position: 'relative' }}>
        <Box sx={{ marginBottom: 2 }}>
          <ErrorDisplay
            message={error}
            type={'error'}
            clearMessage={clearError}
          />
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ ...theme.FullForm.form, gap: 4 }}
        >
          {originalScanDate && (
            <TextField
              label="Currently Scheduled Scan Date"
              name="currentScheduledDate"
              value={originalScanDate.format('MMMM D, YYYY')}
              variant="filled"
              inputProps={{ readOnly: true }}
              fullWidth
            />
          )}

          <AvailableScanDates
            availableDates={availableDates}
            selectedDateIndex={selectedDateIndex}
            hoveredDateIndex={hoveredDateIndex}
            setHoveredDateIndex={setHoveredDateIndex}
            handleDateSelect={(index) => handleDateSelect(index, availableDates, setSelectedDateIndex, setFormData)}
            showMoreButtonVisible={showMoreButtonVisible}
            fetchMoreDates={() => fetchMoreDates(lastFetchedDate, setAvailableDates, setLastFetchedDate, setError)}
          />

          <Box sx={{ ...theme.FullForm.form, gap: 2, marginBottom: 2 }}>
            <div style={{ paddingLeft: 6 }}>List areas of concern for this scan:</div>
            <AddableList
              label="New Concern"
              items={formData.concern}
              onAdd={(value) => addItem('concern', value)}
              onRemove={(index) => removeItem('concern', index)}
              disabled={loading}
              isObject={false}
            />
          </Box>
          <Box sx={{ ...theme.FullForm.form, gap: 2, marginBottom: 4 }}>
            <div style={{ paddingLeft: 6 }}>List specific supplements to scan for (max 5):</div>
            <AddableList
              label="New Supplement"
              items={formData.supplements}
              onAdd={(value) => addItem('supplements', value)}
              onRemove={(index) => removeItem('supplements', index)}
              disabled={loading}
              isObject={false}
              maxItems={5}
            />
          </Box>
          <div>
            <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
              <GeneralSelect
                value={formData.firstFocusScan}
                onChange={(e) => setFormData({ ...formData, firstFocusScan: e.target.value })}
                items={scanList}
                name="firstFocusScan"
                label="Focus Scan Request 1"
                sx={{ flex: 1 }}
                disabled={loading}
              />
              <GeneralSelect
                value={formData.secondFocusScan}
                onChange={(e) => setFormData({ ...formData, secondFocusScan: e.target.value })}
                items={scanList}
                name="secondFocusScan"
                label="Focus Scan Request 2"
                sx={{ flex: 1 }}
                disabled={loading}
              />
            </Box>
            <div style={{ fontSize: 14, display: 'flex', alignItems: 'center', marginTop: 8, justifyContent: 'flex-end' }}>
              <Button startIcon={<Info sx={{ fontSize: 16 }} />} onClick={() => setFocusInfoModalOpen(true)}>
                Focus Scan Request Info
              </Button>
            </div>
          </div>
          <div>
            <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
              <GeneralSelect
                value={formData.firstMiniScan}
                onChange={(e) => setFormData({ ...formData, firstMiniScan: e.target.value })}
                items={miniScanList}
                name="firstMiniScan"
                label="Mini Scan Request 1"
                sx={{ flex: 1 }}
                disabled={loading}
              />
              <GeneralSelect
                value={formData.secondMiniScan}
                onChange={(e) => setFormData({ ...formData, secondMiniScan: e.target.value })}
                items={miniScanList}
                name="secondMiniScan"
                label="Mini Scan Request 2"
                sx={{ flex: 1 }}
                disabled={loading}
              />
            </Box>
            <div style={{ fontSize: 12, color: theme.Colors.GrayMedium, marginLeft: 4, marginTop: 8 }}>
              Mini scans are not required. If any show up in the Root Cause scan, we will scan those regardless. This is just if you specifically want us to include this in the Root Cause scan. These will not be a separate report; they will be included in the Root Cause report.
            </div>
            <div style={{ fontSize: 14, display: 'flex', alignItems: 'center', marginTop: 8, justifyContent: 'flex-end' }}>
              <Button startIcon={<Info sx={{ fontSize: 16 }} />} onClick={() => setMiniInfoModalOpen(true)}>
                Mini Scan Info
              </Button>
            </div>
          </div>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.multiHomeDiscount}
                onChange={handleCheckboxChange}
                name="multiHomeDiscount"
                color="primary"
                disabled={loading}
              />
            }
            label="Are you scheduling multiple scans for this household (they may be eligible for a discount)?"
          />
          <div style={theme.SubHeadingText}>
            Cancel before the kit is mailed for a full refund. After mailing, you can reschedule or cancel, but the deposit will not be refunded. The balance will be charged on the scan date.
          </div>
          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{ flex: 1 }}
              onClick={() => navigate(`/clients/${clientId}/scans/${scanId}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ flex: 3 }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute', borderRadius: 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
      <ConfirmationModal
        open={focusInfoModalOpen}
        onClose={() => setFocusInfoModalOpen(false)}
        onConfirm={() => setFocusInfoModalOpen(false)}
        title="Focus Scan Request Information"
        description="Below is a list of available focus scans and their descriptions."
        confirmButtonText="Close"
        hideCancelButton={true}
        children={<FocusScansList />}
      />
      <ConfirmationModal
        open={miniInfoModalOpen}
        onClose={() => setMiniInfoModalOpen(false)}
        onConfirm={() => setMiniInfoModalOpen(false)}
        title="Mini Scan Request Information"
        description="Below is a list of available mini scans and their descriptions."
        confirmButtonText="Close"
        hideCancelButton={true}
        children={<MiniScansList />}
      />
    </div>
  );
}